import React from "react";
import { i18n } from "./i18nService";
import { redirectTo, trackAndRedirectHandler } from "./navigationService";
import { trackEvent } from "./analyticsService";
import { getVoucherDurationText } from "./copyService";
import { historyMethods } from "../utils/historyMethods";
import { routes } from "../routes";
import { getAppState } from "../state";
import { getGlobalButtonInfo, getGlobalButtonFocusPath } from "../errors/globalButtons";
import { showAndReportUserRelatedError, getInternetConnectivityModalConfig } from "../errors/utils";
import { logout } from "./authenticationService";
import { displayCWPrices, displayEpisodePrices, expiredContent } from "../utils/tvodUtils";
import { buyAssetDetails } from "./mediaService";
import { mergeAndFormatPromptConfig } from "../utils/dataFormatter";
//import { getGlobalConfig } from "./configService";
import { config } from "../config/config";
import { secondLevelMenu } from "../components/MainMenuContainer/menuItems";
import { getSdk } from "./sdkService";
import { Icon } from "./visualLibraryService";

const SIGNUP = "signup";
const LOGIN_SIGNUP = "login_signup";
const LOGIN = "login";
const RETRY = "retry";
const BACK = "back";
const CLOSE = "close";
const RENT = "rent";
const PURCHASE = "purchase";
const CANCEL = "cancel";
const DETAIL = "detail";
const CW = "cw";
const MY_LIST = "library";
const SEND_TO_PHONE = "SendToPhone";
const SIGNUP_PROMPT = "signupPrompt";
const MANAGE_SUBSCRIPTION = "manageSubscription";
const LATER = "later";
const NOTHANKS = "noThanks";
const LOGIN_PROMPT = "loginPrompt";
const VISIT_SITE = "visitSite";
const CARDS_CAROUSEL = "videoCommerceCard";

const modal_type = "VERTICAL_SIDEBAR_POPUP";
const VIDEO_COMMERCE_QR_MODAL = "VIDEO_COMMERCE_QR_MODAL";
let modalCloseTimer = null;

/**
 * Shows a resolution modal in case the received condition is a truthy value, to let user
 * choose between 4K or HD content playback. Needs a React component class context since
 * this method is making use of this.props and other inner context properties (this.play() method).
 * @param {Boolean|Object} condition - Condition that determines whether resolution modal
 * should be shown or not.
 * @public
 */
export async function showResolutionModal(condition) {
  const {
    stateActions: { showGlobalModal },
    setFocus
  } = this.props;
  if (condition) {
    const selectQualityModalConfig = {
      modalText: i18n.get("global.4Ksupported"),
      buttons: [
        {
          label: i18n.get("global.play4K"),
          focusPath: "play4KModal_passThru",
          buttonHandler: () => this.play(true, true)
        },
        {
          label: i18n.get("global.playHD"),
          focusPath: "playHDModal_passThru",
          buttonHandler: () => this.play(false, true)
        }
      ]
    };
    showGlobalModal(selectQualityModalConfig);
    setFocus(selectQualityModalConfig.buttons[0].focusPath);
  } else {
    this.play(false);
  }
}

/**
 * Shows an 'exit signup' modal in case user press back button (either on remote or keyboard) during
 * the signup process to exit the flow. Needs a React component class context since
 * this method is making use of this.props and other inner context properties (this.closeModal() method).
 * @public
 */
export function showExitSignupModal() {
  const {
    stateActions: { showGlobalModal },
    setFocus
  } = this.props;

  const exitModalConfig = {
    modalText: `${i18n.get("global.modals.quit.paragraph1")}${i18n.get("global.modals.quit.paragraph2")}`,
    buttons: [
      {
        label: i18n.get("global.cancel"),
        focusPath: "noOkModal_passThru",
        buttonHandler: () => this.closeModal()
      },
      {
        label: i18n.get("global.yes"),
        focusPath: "okModal_passThru",
        buttonHandler: () => redirectTo.call(this, routes.home, null, historyMethods.REPLACE)
      }
    ],
    dismissHandler: () => this.closeModal()
  };
  showGlobalModal(exitModalConfig);
  setFocus(exitModalConfig.buttons[0].focusPath);
}

// TODO: Use this method across application to avoid generating multiple class methods that look exactly the same.
/**
 * Hides a modal and make focus on a provided path. Needs a React component class context since
 * this method is making use of this.props.
 * @param {String} defaultFocusPath - Path where to focus at when modal is closed.
 * @public
 */
export function closeModal(defaultFocusPath) {
  const {
    stateActions: { hideGlobalModal },
    setFocus
  } = this.props;
  hideGlobalModal();
  setFocus(defaultFocusPath);
}

/**
 * Gets the modal for when the user is not logged and tries to play some content. In case device is
 * eligible to have a voucher, an extra button will appear to make reference to signup process, as in the modal text.
 */
export function showNotLoggedModal(
  { nextPageReference = undefined, backPageReference = undefined },
  closeCallback = null,
  tvodAssetData = null,
  showVoucherButton = true,
  dismissable = true
) {
  const buttons = [
    {
      ...getGlobalButtonInfo(LOGIN_SIGNUP),
      buttonHandler: redirectTo.bind(
        this,
        routes.pinSignup,
        {
          ...(nextPageReference && { nextPage: nextPageReference }),
          ...(backPageReference && { backDestination: backPageReference }),
          ...tvodAssetData
        },
        historyMethods.REPLACE
      )
    },
    {
      ...getGlobalButtonInfo(LOGIN),
      buttonHandler: redirectTo.bind(
        this,
        routes.pin,
        {
          ...(nextPageReference && { nextPage: nextPageReference }),
          ...(backPageReference && { backDestination: backPageReference })
        },
        historyMethods.REPLACE
      )
    },
    ...(!!getAppState().voucherData && showVoucherButton
      ? [
          {
            ...getGlobalButtonInfo(SIGNUP, getVoucherDurationText(getAppState().voucherData?.duration?.duration)),
            buttonHandler: () => {
              trackEvent(SIGNUP, "details_popup_user_selects_joinnow");
              redirectTo.call(this, routes.signup);
            }
          }
        ]
      : []),
    {
      ...getGlobalButtonInfo("cancel"),
      buttonHandler: closeCallback || this.closeModal
    }
  ];
  showAndReportUserRelatedError.call(
    this,
    {
      modalTitle: i18n.get("pages.detail.invalidAction.loginSignupHeading"),
      modalText:
        !!getAppState().voucherData && showVoucherButton
          ? `${i18n.get("pages.detail.invalidAction.joinToPlay.paragraph1")}${i18n.get(
              "pages.detail.invalidAction.joinToPlay.paragraph2"
            )}`
          : i18n.get("pages.detail.invalidAction.loginToPlay"),
      buttons,
      dismissable
    },
    { event: "Playback", status: "info", errorName: "loginToPlayError" }
  );
  this.props.setFocus(getGlobalButtonFocusPath(LOGIN_SIGNUP));
}

/**
 * Gets the modal for when the user is logged but its account status is not active, so it won't allow to
 * play any content.
 */
export function showInvalidStatusModal(closeCallback = null) {
  let modalText = i18n.get("global.errors.account.invalidStatus");
  let focusPath = CLOSE;
  let buttons = [
    ...(!!getAppState().voucherData
      ? [
          {
            ...getGlobalButtonInfo(
              "startWatching",
              getVoucherDurationText(getAppState().voucherData.duration.duration)
            ),
            buttonHandler: () => {
              trackEvent(SIGNUP, "details_popup_user_selects_startnow");
              redirectTo.call(this, routes.redeem);
            }
          }
        ]
      : []),
    {
      ...getGlobalButtonInfo(CLOSE),
      buttonHandler: closeCallback || this.closeModal
    }
  ];
  if (!!getAppState().voucherData) {
    focusPath = "startWatching";
    modalText = `${i18n.get("global.errors.account.invalidStatusEVoucher.paragraph1")}${i18n.get(
      "global.errors.account.invalidStatusEVoucher.paragraph2"
    )}`;
  }

  showAndReportUserRelatedError.call(this, {
    modalText,
    buttons
  });
  this.props.setFocus(getGlobalButtonFocusPath(focusPath));
}

/**
 * Creates a global modal where the options to be shown are either to retry the request or going back in history
 * (or just this last one).
 */
export function showRetryOrBackModal(catchError) {
  let focusPath = RETRY;
  const retry = async () => {
    this.props.stateActions.hideGlobalModal();
    this.setState({
      titleData: await this.fetchData()
    });
  };
  const back = () => {
    this.props.stateActions.hideGlobalModal();
    this.onBack();
  };
  const backButton = {
    ...getGlobalButtonInfo(BACK),
    buttonHandler: back
  };

  this.props.resumeNavigation();

  // FIXME: Create an error code for no connection instead of comparing the string
  if (getAppState().isOnline && catchError && catchError.message.indexOf("timeout") > -1) {
    this.props.stateActions.showGlobalModal(
      getInternetConnectivityModalConfig.call(this, {
        retryImplementation: retry,
        backImplementation: back,
        needsDismissableProp: false,
        isTimeout: true
      })
    );
  } else if (
    (catchError && (catchError.status === 400 || catchError.status === 404) && catchError.error_code === "51001") ||
    this.state.unAuthorizedAccess
  ) {
    // FIXME: Handle this kind of errors from the SDK itself not from the client app
    focusPath = BACK;
    this.setState({ unAuthorizedAccess: true });
    this.props.stateActions.showGlobalModal({
      dismissable: false,
      modalText: i18n.get("pages.detail.errors.unauthorizedAccessToTitle"),
      buttons: [backButton]
    });
  } else if (!getAppState().isOnline && !catchError.status && !catchError.response) {
    this.props.stateActions.showGlobalModal(
      getInternetConnectivityModalConfig.call(this, {
        retryImplementation: retry,
        backImplementation: back
      })
    );
  } else {
    this.props.stateActions.showGlobalModal({
      dismissable: false,
      modalText: i18n.get("global.errors.genericError"),
      buttons: [
        {
          ...getGlobalButtonInfo(RETRY),
          buttonHandler: retry
        },
        backButton
      ]
    });
  }
  this.props.setFocus(getGlobalButtonFocusPath(focusPath));
}

/**
 * Generates a single button for the modal.
 * @param {String} identifier - Button identifier.
 * @param {Function} callback - Function to be called when pressing the button.
 */
export function showSingleModalButton(identifier, callback) {
  return [
    {
      ...getGlobalButtonInfo(identifier),
      buttonHandler: callback
    }
  ];
}

export async function showForceLogoutError() {
  const {
    stateActions: { showGlobalModal },
    setFocus
  } = this.props;
  if (!!getAppState().forceLogout) {
    if (!!getAppState().isOnline) {
      const buttons = [
        {
          ...getGlobalButtonInfo("accept"),
          buttonHandler: () => {
            this.props.stateActions.hideGlobalModal();
            getAppState().forceLogout = false;
            logout(true, "force_logout");
            this.props.stateActions.userLoggedOut();
            redirectTo.call(this, routes.pin);
          }
        }
      ];
      showGlobalModal({
        modalText: i18n.get("global.errors.forceLogout.title"),
        buttons
      });
      setFocus(getGlobalButtonFocusPath("accept"));
    }
  }
}

export function getUnmanageableModal() {
  const { setFocus } = this.props;
  let text = `pages.nonchangeable.unManagedModal.text`;
  setTimeout(() => {
    setFocus("okButtonModalFocusPath");
  }, 0);
  return {
    dismissable: false,
    modalText: i18n.get(text),
    buttons: [
      {
        label: i18n.get("pages.nonchangeable.unManagedModal.okButton"),
        displayBlock: true,
        focusPath: "okButtonModalFocusPath",
        buttonHandler: () => redirectTo.call(this, routes.promotions, null, historyMethods.REPLACE)
      }
    ]
  };
}

export function showExpiredContentModal(closeCallback, data, eventData) {
  const { stateActions, setFocus } = this.props;
  const fromEpisode = data.rentCtaData || data.purchaseCtaData ? true : false;
  const fromCW = data.products ? true : false;

  const episodeRentLabel = data.rentCtaData && displayEpisodePrices(data).rentPrice;
  const episodePurchaseLabel = data.purchaseCtaData && displayEpisodePrices(data).purchasePrice;

  const cwRentPrice = data.products && displayCWPrices(data).rentPrice;
  const cwPurchasePrice = data.products && displayCWPrices(data).purchasePrice;
  const cwRentData = data.products ? displayCWPrices(data).rentCtaData : null;
  const cwPurchaseData = data.products ? displayCWPrices(data).purchaseCtaData : null;

  const contentStatus = data.license && expiredContent(data.license.state) ? true : false;

  const buttons = [
    (episodeRentLabel || cwRentPrice) && {
      ...getGlobalButtonInfo(RENT, episodeRentLabel ? episodeRentLabel : cwRentPrice),
      buttonHandler: trackAndRedirectHandler.bind(
        this,
        data.isOnDetail && fromEpisode ? DETAIL : fromCW ? CW : data.isOnMyList && MY_LIST,
        contentStatus ? "rent_now_again" : "rent_now_cta",
        routes.buyasset,
        buyAssetDetails.call(this, "rent", fromEpisode ? data.rentCtaData : fromCW && cwRentData),
        "TVOD",
        {
          ...eventData,
          price: cwRentPrice ? data.products.rent?.new : data.rentCtaData.newPrice,
          currency: cwRentPrice ? data.products.rent?.currency : data.rentCtaData.newPriceCurrency,
          modalType: "TVOD-Rent"
        }
      )
    },
    (episodePurchaseLabel || cwPurchasePrice) && {
      ...getGlobalButtonInfo(PURCHASE, episodePurchaseLabel ? episodePurchaseLabel : cwPurchasePrice),
      buttonHandler: trackAndRedirectHandler.bind(
        this,
        fromEpisode ? DETAIL : fromCW ? CW : data.isOnMyList && MY_LIST,
        "buy_now_cta",
        routes.buyasset,
        buyAssetDetails.call(this, "purchase", fromEpisode ? data.purchaseCtaData : fromCW && cwPurchaseData),
        "TVOD",
        {
          ...eventData,
          price: cwPurchasePrice ? data.products.purchase?.new : data.purchaseCtaData.newPrice,
          currency: cwPurchasePrice ? data.products.purchase?.currency : data.purchaseCtaData.newPriceCurrency,
          modalType: "TVOD-Buy"
        }
      )
    },
    {
      ...getGlobalButtonInfo(CANCEL),
      buttonHandler: closeCallback || this.closeModal
    }
  ].filter(Boolean);
  stateActions.showGlobalModal({
    modalTitle:
      data.isOnMyList || fromCW
        ? i18n.get("global.modals.expiredContent.title")
        : fromEpisode && i18n.get("global.modals.episodeContent.title"),
    modalText:
      data.isOnMyList || fromCW
        ? i18n.get("global.modals.expiredContent.description")
        : fromEpisode && i18n.get("global.modals.episodeContent.description"),
    buttons
  });
  setFocus(getGlobalButtonFocusPath(episodeRentLabel || cwRentPrice ? RENT : PURCHASE));
}

/**
 * Gets the modal for when the user click on send to my phone button the modal show the website URL,
 * QR code and modal closing timer.
 */
export function showQRCodeModal(closeCallback, titleURL, qrCodeURL = false, isShowPauseAd = false) {
  if (modalCloseTimer) {
    clearTimeout(modalCloseTimer);
  }
  const {
    stateActions: { showGlobalModal },
    setFocus
  } = this.props;
  const fromPlayer = this.props.history?.location?.pathname === "/player";
  const buttons = [
    {
      ...getGlobalButtonInfo("close"),
      buttonHandler: () => closeCallback(isShowPauseAd ? VISIT_SITE : SEND_TO_PHONE) || this.closeModal
    }
  ];
  const showModal = showGlobalModal({
    modalTitle: `${i18n.get("pages.player.ads.QRScreenTitle")} <br /> ${titleURL.split("/")[2]}`,
    qrCodeURL: qrCodeURL ? qrCodeURL : titleURL,
    modalClosingTimer: i18n.get("pages.player.ads.modalClosingTimer"),
    buttons
  });
  if (showModal) {
    modalCloseTimer = setTimeout(() => {
      if (fromPlayer && !isShowPauseAd) {
        this.player.play();
        closeCallback(SEND_TO_PHONE) || this.closeModal();
      } else if (isShowPauseAd) {
        closeCallback(VISIT_SITE) || this.closeModal();
      } else {
        closeCallback() || this.closeModal();
      }
    }, 31000);
  }
  setFocus(getGlobalButtonFocusPath("close"));
}

export function QRModal(closeCallback, qrCodeURL = false, isDismissable = true) {
  const {
    stateActions: { showGlobalModal },
    setFocus
  } = this.props;
  const icon = <Icon name={"popup_back"} viewBoxValue="0 0 40 40" height="2.5rem" fill="#ffffff" />;

  const showModal = showGlobalModal({
    modalTitle: `${i18n.get("pages.player.twist.modalTitle")} `,
    modalText: `${i18n.get("pages.player.twist.qrTitle")}`,
    qrCodeURL: qrCodeURL,
    dismissable: isDismissable,
    backIcon: icon
  });
  trackEvent("page_view", "migration_qr", "page_view");
}

/**
 * Gets the modal for time based prompt  for the Guest user and inactive users for signup and premium subscriptions.
 * show this prompt once per session on  all pages except player. This popup under new designs.
 */
export function showSignupPremiumPrompt(
  { nextPageReference = undefined, backPageReference = undefined },
  closeCallback = null,
  tvodAssetData = null
) {
  const promptFeatures = getAppState().userLogged
    ? config.get().avod?.prompt?.premium?.features
    : config.get().avod?.prompt?.signup?.features || [];

  const buttons = [
    !getAppState().userLogged && {
      ...getGlobalButtonInfo(SIGNUP_PROMPT),
      buttonHandler: redirectTo.bind(
        this,
        routes.pinSignup,
        {
          ...(nextPageReference && { nextPage: nextPageReference }),
          ...(backPageReference && { backDestination: backPageReference }),
          ...tvodAssetData
        },
        historyMethods.REPLACE
      )
    },
    !getAppState().userLogged && {
      ...getGlobalButtonInfo(LOGIN_PROMPT),
      buttonHandler: redirectTo.bind(
        this,
        routes.pin,
        {
          ...(nextPageReference && { nextPage: nextPageReference }),
          ...(backPageReference && { backDestination: backPageReference })
        },
        historyMethods.REPLACE
      )
    },
    getAppState().userLogged && {
      ...getGlobalButtonInfo(MANAGE_SUBSCRIPTION),
      buttonHandler: redirectTo.bind(
        this,
        routes.brands,
        {
          ...(nextPageReference && { nextPage: nextPageReference }),
          ...(backPageReference && { backDestination: backPageReference })
        },
        historyMethods.REPLACE
      )
    },

    {
      ...getGlobalButtonInfo(getAppState().userLogged ? NOTHANKS : LATER),
      buttonHandler: closeCallback || this.closeModal,
      noborder: true
    }
  ].filter(button => button);

  showAndReportUserRelatedError.call(this, {
    modalTitle: getAppState().userLogged
      ? i18n.get("global.prompt.premium.title")
      : i18n.get("global.prompt.signup.title"),
    modalText: getAppState().userLogged
      ? i18n.get("global.prompt.premium.subTitle")
      : i18n.get("global.prompt.signup.subTitle"),
    buttons,
    type: modal_type,
    showPreemiumLogo: getAppState().userLogged ? true : false,
    isUserLogged: getAppState().userLogged,
    features:
      mergeAndFormatPromptConfig(
        promptFeatures,
        getAppState().userLogged
          ? i18n.get("global.prompt.premium.features")
          : i18n.get("global.prompt.signup.features")
      ) || [],
    promptRef: this.promptRef
  });
  this.props.setFocus(getGlobalButtonFocusPath(getAppState().userLogged ? MANAGE_SUBSCRIPTION : SIGNUP_PROMPT));
  return getGlobalButtonFocusPath(getAppState().userLogged ? MANAGE_SUBSCRIPTION : SIGNUP_PROMPT);
}
export function showCategoriesMenuModal() {
  var sLevelMenu = secondLevelMenu(this, redirectTo, historyMethods, i18n);
  const buttons = [
    ...sLevelMenu,
    {
      ...getGlobalButtonInfo("back"),
      buttonHandler: this.closeModal,
      isModalCloseBtn: true
    }
  ];
  const {
    stateActions: { showGlobalModal },
    setFocus
  } = this.props;
  const categoriesMenuModalConfig = {
    modalTitle: i18n.get("global.sections.categoriesModalTitle"),
    modalText: "",
    buttons,
    isCategoriesModal: true,
    dismissHandler: () => this.closeModal()
  };
  showGlobalModal(categoriesMenuModalConfig);
  setTimeout(() => {
    setFocus(sLevelMenu[0].focusPath);
  }, 0);
  getAppState().secLevelMenuPrevPath = this?.props?.history?.location?.pathname || null;
}
export function showAppExitConfirmationModal(closeCallback, dismissable = true) {
  const {
    stateActions: { showGlobalModal },
    setFocus
  } = this.props;
  const showAppExitConfirmationModalConfig = {
    modalTitle: i18n.get("global.modals.exit.title"),
    modalText: i18n.get("global.modals.exit.text"),
    buttons: [
      {
        ...getGlobalButtonInfo("no"),
        buttonHandler: closeCallback || this.closeModal
      },
      {
        ...getGlobalButtonInfo("yes"),
        buttonHandler: () => getSdk().systemManager.application.exit()
      }
    ],
    dismissable
  };
  showGlobalModal(showAppExitConfirmationModalConfig);
  setFocus(getGlobalButtonFocusPath("no"));
}

export function showContentNotAccessibleModal(callback, data) {
  const { name = "", isSVODContent = false, isTVODContent = false, dismissable = true } = data || {};
  const { stateActions, setFocus } = this.props;
  const modalTitle = isSVODContent
    ? i18n.get("global.errors.notAccessible.title")
    : isTVODContent
    ? i18n.get("global.errors.TVODContentRestriction.title")
    : i18n.get("global.errors.DeepLinkRestriction.title");
  const modalText = isSVODContent
    ? i18n.get("global.errors.notAccessible.invalidProfileSelected", name, name)
    : isTVODContent
    ? i18n.get("global.errors.TVODContentRestriction.description", name, name)
    : i18n.get("global.errors.DeepLinkRestriction.description");
  const buttons = [
    {
      ...getGlobalButtonInfo("ok"),
      buttonHandler: callback || this.closeModal
    }
  ];
  stateActions.showGlobalModal({
    modalTitle,
    modalText,
    buttons,
    dismissable
  });
  setFocus(getGlobalButtonFocusPath("ok"));
}

export function showUserAccountCallFailedModal() {
  const { stateActions, setFocus } = this.props;
  const buttons = [
    {
      ...getGlobalButtonInfo("retry"),
      buttonHandler: async () => {
        stateActions.setUserAccountFailed(false);
        window.location.href = "/";
      }
    }
  ];
  stateActions.showGlobalModal({
    modalTitle: i18n.get("global.errors.multiProfile.userAccountFailedHeader"),
    modalText: i18n.get("global.errors.multiProfile.userAccountFailedErrorText"),
    buttons
  });
  setFocus(getGlobalButtonFocusPath("retry"));
}

export function showMultipleProfileErrorModal(focusPath) {
  this.props.stateActions.showGlobalModal({
    modalTitle: i18n.get("global.errors.multiProfile.userAccountFailedHeader"),
    modalText: i18n.get("global.errors.multiProfile.userAccountFailedErrorText"),
    buttons: [
      {
        ...getGlobalButtonInfo("retry"),
        buttonHandler: async () => {
          this.props.stateActions.hideGlobalModal();
          this.props.setFocus(focusPath);
        }
      }
    ]
  });
  this.props.setFocus(getGlobalButtonFocusPath("retry"));
}

export function showDeviceLimitError(closeCallback) {
  const { stateActions, setFocus } = this.props;
  const buttons = [
    {
      ...getGlobalButtonInfo("ok"),
      buttonHandler: closeCallback || this.closeModal
    }
  ];
  stateActions.showSettingsModal({
    modalText: i18n.get("global.errors.devices.deviceLimitError"),
    buttons
  });
  setFocus(getGlobalButtonFocusPath("ok"));
}

export function showVideoCommerceModal(closeCallback, data) {
  const { stateActions } = this.props;
  let baseUrl = null;
  if (data.link) {
    baseUrl = new URL(data.link).origin;
  }
  stateActions.showGlobalModal({
    type: VIDEO_COMMERCE_QR_MODAL,
    modalTitle: data.title,
    modalText: i18n.get("pages.player.videoCommerce.modal.titleShopMore"),
    qrCodeURL: data.link || "",
    videoCommerceProductDetail: {
      currentPrice: data.currentPrice,
      originalPrice: data.originalPrice,
      brandUrl: baseUrl,
      qrCodeTitle: i18n.get("pages.player.videoCommerce.modal.qrCodeTitle")
    },
    onClickBack: () => closeCallback(CARDS_CAROUSEL)
  });
}
